import { z } from 'zod';

export const BattleStatusSchema = z.enum(['in_progress', 'completed']);
export type BattleStatus = z.infer<typeof BattleStatusSchema>;

export const BattleWinnerSchema = z.enum(['attacker', 'defender', 'draw']);
export type BattleWinner = z.infer<typeof BattleWinnerSchema>;

export const BattleEventSchema = z.object({
  tick: z.number().int(),
  type: z.enum(['attack', 'shield_break', 'ship_destroyed', 'battle_end', 'retreat_started']),
  attackerId: z.string().optional(),
  targetId: z.string().optional(),
  attackerName: z.string().optional(),
  targetName: z.string().optional(),
  shieldDamage: z.number().int().optional(),
  hullDamage: z.number().int().optional(),
  message: z.string(),
});
export type BattleEvent = z.infer<typeof BattleEventSchema>;

export const BattleReportSchema = z.object({
  id: z.string().uuid(),
  status: BattleStatusSchema,
  winner: BattleWinnerSchema.nullable(),
  attackerFleetId: z.string().uuid(),
  defenderFleetId: z.string().uuid(),
  attackerCivId: z.string().uuid(),
  defenderCivId: z.string().uuid(),
  systemId: z.string().uuid(),
  attackerLosses: z.number().int(),
  defenderLosses: z.number().int(),
  log: z.array(BattleEventSchema),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});
export type BattleReport = z.infer<typeof BattleReportSchema>;

// M5/3: Battle Replay

export const ReplayShipSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  shipClass: z.enum(['corvette', 'frigate', 'cruiser', 'scout', 'construction']),
  maxHull: z.number().int(),
  maxShield: z.number().int(),
  armor: z.number().int(),
  side: z.enum(['attacker', 'defender']),
});
export type ReplayShip = z.infer<typeof ReplayShipSchema>;

export const BattleReplayDataSchema = z.object({
  attackerFleetId: z.string().uuid(),
  defenderFleetId: z.string().uuid(),
  ships: z.array(ReplayShipSchema),
});
export type BattleReplayData = z.infer<typeof BattleReplayDataSchema>;

export const BattleReplaySchema = z.object({
  battleId: z.string().uuid(),
  replayData: BattleReplayDataSchema,
  log: z.array(BattleEventSchema),
  winner: BattleWinnerSchema.nullable(),
});
export type BattleReplay = z.infer<typeof BattleReplaySchema>;

export const AttackCommandSchema = z.object({
  attackerFleetId: z.string().uuid(),
  defenderFleetId: z.string().uuid(),
  idempotencyKey: z.string().min(1).max(128),
});
export type AttackCommand = z.infer<typeof AttackCommandSchema>;

// M5/2: System attack / conflict

export const ConflictStatusSchema = z.enum(['battle_in_progress', 'occupied', 'captured', 'failed']);
export type ConflictStatus = z.infer<typeof ConflictStatusSchema>;

export const ConflictReportSchema = z.object({
  id: z.string().uuid(),
  systemId: z.string().uuid(),
  attackerCivId: z.string().uuid(),
  attackerFleetId: z.string().uuid(),
  defenderCivId: z.string().uuid(),
  battleId: z.string().uuid().nullable(),
  status: ConflictStatusSchema,
  battleWinner: BattleWinnerSchema.nullable(),
  occupationStartedAt: z.string().datetime().nullable(),
  occupationEndsAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type ConflictReport = z.infer<typeof ConflictReportSchema>;

export const SystemAttackCommandSchema = z.object({
  attackerFleetId: z.string().uuid(),
  idempotencyKey: z.string().min(1).max(128),
});
export type SystemAttackCommand = z.infer<typeof SystemAttackCommandSchema>;
